import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface TeamStartState {
  eventCode: string | null;
  teamName: string;
  initiatorEmail: string;
  startTime: number; // Timestamp when start was initiated
  joinedMembers: string[]; // Emails of members who have joined
}

interface TeamStartContextType {
  currentTeamStart: TeamStartState | null;
  initiateTeamStart: (eventCode: string, teamName: string, initiatorEmail: string) => void;
  joinTeamStart: (eventCode: string, memberEmail: string) => void;
  clearTeamStart: () => void;
}

const TeamStartContext = createContext<TeamStartContextType | undefined>(undefined);

const TEAM_START_STORAGE_KEY = 'loop_team_start';

export function TeamStartProvider({ children }: { children: ReactNode }) {
  const [currentTeamStart, setCurrentTeamStart] = useState<TeamStartState | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(TEAM_START_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Check if the start session is still valid (within 5 seconds)
        if (Date.now() - parsed.startTime < 5000) {
          setCurrentTeamStart(parsed);
        } else {
          localStorage.removeItem(TEAM_START_STORAGE_KEY);
        }
      } catch {
        localStorage.removeItem(TEAM_START_STORAGE_KEY);
      }
    }
  }, []);

  // Listen for storage changes (for cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === TEAM_START_STORAGE_KEY) {
        if (e.newValue) {
          try {
            const parsed = JSON.parse(e.newValue);
            if (Date.now() - parsed.startTime < 5000) {
              setCurrentTeamStart(parsed);
            }
          } catch {
            setCurrentTeamStart(null);
          }
        } else {
          setCurrentTeamStart(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const initiateTeamStart = (eventCode: string, teamName: string, initiatorEmail: string) => {
    const newState: TeamStartState = {
      eventCode,
      teamName,
      initiatorEmail,
      startTime: Date.now(),
      joinedMembers: [initiatorEmail],
    };
    
    setCurrentTeamStart(newState);
    localStorage.setItem(TEAM_START_STORAGE_KEY, JSON.stringify(newState));
  };

  const joinTeamStart = (eventCode: string, memberEmail: string) => {
    setCurrentTeamStart((prev) => {
      if (!prev || prev.eventCode !== eventCode) return prev;
      
      if (prev.joinedMembers.includes(memberEmail)) return prev;
      
      const updated = {
        ...prev,
        joinedMembers: [...prev.joinedMembers, memberEmail],
      };
      
      localStorage.setItem(TEAM_START_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const clearTeamStart = () => {
    setCurrentTeamStart(null);
    localStorage.removeItem(TEAM_START_STORAGE_KEY);
  };

  return (
    <TeamStartContext.Provider
      value={{
        currentTeamStart,
        initiateTeamStart,
        joinTeamStart,
        clearTeamStart,
      }}
    >
      {children}
    </TeamStartContext.Provider>
  );
}

export function useTeamStart() {
  const context = useContext(TeamStartContext);
  if (!context) {
    throw new Error('useTeamStart must be used within TeamStartProvider');
  }
  return context;
}
